import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Eyebrow,
  DisplayHeading,
  BodyCopy,
  Reveal,
  Button,
} from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import { color } from "@/lib/theme";
import { fadeUp, viewport } from "@/lib/motion";
import {
  TrustLayerHero,
  QuestionConverge,
  ClaimEvidenceTrust,
  SecurityGovernanceConverge,
  ContinuousLifecycle,
  TrustArchitecture,
  GlobalTrustNetwork,
  ProductArchitecture,
} from "@/components/AboutVisuals";

/* -------- 01–04 narrative -------- */
const CHAPTERS = [
  {
    n: "01",
    title: "The question is changing.",
    body: "Organizations are adopting AI at extraordinary speed. But when a customer, board member, auditor or regulator asks, “Can you prove this AI is safe and governed?” an internal dashboard is no longer enough. They need evidence.",
    Visual: QuestionConverge,
  },
  {
    n: "02",
    title: "Trust must be verifiable.",
    body: "Claims are easy to make and difficult to validate. Meira is built around a different principle: important claims about AI should be supported by credible evidence that authorized stakeholders can independently verify.",
    Visual: ClaimEvidenceTrust,
  },
  {
    n: "03",
    title: "Security and governance belong together.",
    body: "You cannot meaningfully govern AI you have never tested. And testing without ownership, controls and evidence becomes another isolated report. Meira connects both disciplines through one assurance layer.",
    Visual: SecurityGovernanceConverge,
  },
  {
    n: "04",
    title: "Assurance must evolve with AI.",
    body: "AI systems change. Models are updated. Agents gain capabilities. Dependencies shift. Regulations evolve. Assurance cannot remain a one-time snapshot — it must be continuously renewable and re-provable.",
    Visual: ContinuousLifecycle,
  },
];

const BELIEFS = [
  ["Innovation and accountability are not opposites.", "brand"],
  ["AI assurance should be evidence-based.", "brand"],
  ["Security and governance belong together.", "brand"],
  ["Trust should be independently verifiable.", "valid"],
  ["AI governance must evolve with AI.", "brand-deep"],
];

function ManifestoWall() {
  const [active, setActive] = useState(0);
  return (
    <div className="mt-8 flex flex-col sm:mt-12" data-testid="manifesto-wall">
      {BELIEFS.map(([text, tone], i) => {
        const on = active === i;
        return (
          <button
            key={text}
            data-testid={`belief-${i}`}
            onMouseEnter={() => setActive(i)}
            onFocus={() => setActive(i)}
            onClick={() => setActive(i)}
            aria-pressed={active === i}
            className="group flex items-center gap-3.5 border-b border-line py-5 text-left transition-colors duration-300 focus:outline-none sm:gap-5 sm:py-7"
          >
            <span className="font-mono text-[11px] text-content-faint">{String(i + 1).padStart(2, "0")}</span>
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-300"
              style={{ background: color(on ? tone : "line-bright"), boxShadow: on ? `0 0 8px ${color(tone, 0.4)}` : "none", transform: on ? "scale(1.4)" : "scale(1)" }}
            />
            <span
              className={`font-display text-[19px] font-medium leading-snug transition-colors duration-300 sm:text-2xl md:text-3xl ${on ? "text-content" : "text-content-muted/60"}`}
            >
              {text}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function About() {
  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden pb-14 pt-28 sm:pb-20 sm:pt-36 md:pb-28 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-16 left-1/3 h-[260px] w-[420px] -translate-x-1/2 rounded-full bg-brand/[0.06] blur-[90px] sm:-top-24 sm:h-[420px] sm:w-[820px] sm:blur-[120px]" />
        <Container className="relative">
          <div className="grid items-center [&>*]:min-w-0 gap-10 sm:gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-8">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show">
                <Eyebrow>About Meira</Eyebrow>
              </motion.div>
              <DisplayHeading as="h1" className="mt-6 max-w-2xl text-[34px] leading-[1.06] sm:mt-7 sm:text-5xl md:text-6xl">
                <MaskedLinesInView lines={["Building the trust", "layer for the AI era."]} />
              </DisplayHeading>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-8 max-w-xl">
                <BodyCopy>
                  Meira exists because trust in AI can no longer be assumed — it has to be tested, governed and proven.
                </BodyCopy>
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:mt-9 sm:max-w-none sm:flex-row sm:flex-wrap sm:gap-4">
                <Button to="/platform" variant="outline" data-testid="about-hero-platform">Explore the Platform</Button>
                <Button to="/contact" variant="primary" data-testid="about-hero-demo" className="uppercase tracking-[0.12em]">Book a Demo</Button>
              </motion.div>
            </div>
            <div className="relative">
              <TrustLayerHero />
            </div>
          </div>
        </Container>
      </section>

      {/* ---------------- 01–04 NARRATIVE ---------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <div className="flex flex-col gap-14 sm:gap-20 md:gap-28">
            {CHAPTERS.map(({ n, title, body, Visual }, i) => {
              const flip = i % 2 === 1;
              return (
                <div key={n} className="grid items-center gap-7 sm:gap-10 lg:grid-cols-2 lg:gap-16">
                  <Reveal className={flip ? "lg:order-2" : ""}>
                    <div className="font-display text-5xl font-light text-content/[0.08] sm:text-6xl md:text-7xl">{n}</div>
                    <DisplayHeading className="mt-3 max-w-lg text-3xl leading-tight sm:text-4xl">{title}</DisplayHeading>
                    <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-content-muted sm:text-base">{body}</p>
                  </Reveal>
                  <Reveal i={1} className={flip ? "lg:order-1" : ""}>
                    <Visual />
                  </Reveal>
                </div>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* ---------------- WHY MEIRA EXISTS (signature) ---------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow tone="brand">Why Meira exists</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-3xl text-3xl leading-tight sm:text-4xl md:text-5xl">
            AI is becoming infrastructure. Trust must become infrastructure too.
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-6 max-w-2xl">
              As AI moves deeper into products, operations and decisions, organizations need more than policies and point-in-time assessments. They need a persistent trust layer connecting what an AI system is, what it can do, how it was tested, how it is governed and what evidence supports its assurance.
            </BodyCopy>
          </Reveal>
          <Reveal i={2}>
            <div className="mt-10 sm:mt-14">
              <TrustArchitecture />
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* ---------------- MISSION / VISION ---------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <div className="grid gap-4 sm:gap-5 md:grid-cols-2">
            <Reveal>
              <div className="relative h-full overflow-hidden rounded-lg border border-brand/20 bg-gradient-to-br from-brand/[0.08] to-transparent p-6 sm:p-9">
                <span aria-hidden className="pointer-events-none absolute -right-2 bottom-0 select-none font-display text-[4rem] font-medium leading-none text-content/[0.03] sm:text-[7rem]">MISSION</span>
                <Eyebrow>Mission</Eyebrow>
                <DisplayHeading className="relative mt-4 max-w-md text-[22px] leading-snug sm:mt-5 sm:text-3xl">
                  Make enterprise AI safe, accountable and provable.
                </DisplayHeading>
              </div>
            </Reveal>
            <Reveal i={1}>
              <div className="relative h-full overflow-hidden rounded-lg border border-brand/20 bg-gradient-to-br from-brand/[0.08] to-transparent p-6 sm:p-9">
                <span aria-hidden className="pointer-events-none absolute -right-2 bottom-0 select-none font-display text-[4rem] font-medium leading-none text-content/[0.03] sm:text-[7rem]">VISION</span>
                <Eyebrow tone="brand">Vision</Eyebrow>
                <DisplayHeading className="relative mt-4 max-w-md text-[19px] leading-snug sm:mt-5 sm:text-2xl md:text-3xl">
                  A future where organizations can deploy AI with confidence — because every important system, decision and assurance claim can be governed, explained and backed by credible evidence.
                </DisplayHeading>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* ---------------- WHAT WE BELIEVE ---------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>What we believe</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">
            Five convictions behind Meira.
          </DisplayHeading>
          <ManifestoWall />
        </Container>
      </Section>

      {/* ---------------- GLOBAL BY DESIGN ---------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <div className="grid items-center gap-8 sm:gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
            <div>
              <Reveal><Eyebrow tone="brand">Global by design</Eyebrow></Reveal>
              <DisplayHeading className="mt-6 max-w-lg text-3xl leading-tight sm:text-4xl md:text-5xl">
                AI crosses borders. Trust must travel with it.
              </DisplayHeading>
              <Reveal i={1}>
                <BodyCopy className="mt-6 max-w-lg">
                  Organizations operate across markets, frameworks and regulatory environments. Meira is being designed as a common assurance layer that helps teams connect AI security, governance and evidence across that complexity.
                </BodyCopy>
              </Reveal>
            </div>
            <Reveal i={1}>
              <GlobalTrustNetwork />
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* ---------------- WHAT WE'RE BUILDING ---------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>The platform we’re building</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl md:text-5xl">
            From AI system to verifiable trust.
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-6 max-w-2xl">
              A connected pipeline — not a set of disconnected tools. Each stage writes into the same evidence base, so assurance can be traced end to end.
            </BodyCopy>
          </Reveal>
          <Reveal i={2}>
            <div className="mt-10 sm:mt-14">
              <ProductArchitecture />
            </div>
          </Reveal>
          <p className="mt-8 font-mono text-[11px] tracking-wide text-content-faint">
            Status reflects current build stage. Demonstration data throughout.
          </p>
        </Container>
      </Section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="relative overflow-hidden py-20 sm:py-28 md:py-36">
        <div className="pointer-events-none absolute inset-0 tech-grid opacity-40" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[380px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/[0.05] blur-[130px]" />
        <Container className="relative text-center">
          <div className="mx-auto mb-8 flex max-w-md flex-wrap items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em]" aria-hidden>
            <span className="text-content-muted">Test</span>
            <span className="text-content-faint">→</span>
            <span className="text-content-muted">Govern</span>
            <span className="text-content-faint">→</span>
            <span className="text-brand">Evidence</span>
            <span className="text-content-faint">→</span>
            <span className="text-valid">Prove</span>
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="mx-auto flex justify-center">
            <Eyebrow tone="brand">Work with Meira</Eyebrow>
          </motion.div>
          <DisplayHeading className="mx-auto mt-7 max-w-3xl text-3xl leading-tight sm:text-4xl md:text-5xl">
            <MaskedLinesInView lines={["Let’s make", "your AI provable."]} />
          </DisplayHeading>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={2}>
            <BodyCopy className="mx-auto mt-6 max-w-xl">
              See how Meira connects AI security, governance and assurance into evidence your organization — and authorized stakeholders — can verify.
            </BodyCopy>
            <div className="mx-auto mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:mt-9 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4">
              <Button to="/contact" variant="primary" data-testid="about-cta-demo" className="uppercase tracking-[0.12em]">Book a Demo</Button>
              <Button to="/platform" variant="outline" data-testid="about-cta-platform">Explore the Platform</Button>
            </div>
            <div className="mt-6">
              <Button to="/verify" variant="ghost" data-testid="about-cta-verify" className="text-content-muted">Verify a Certificate →</Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
}
